import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action } from '@ngrx/store';
import {
  AsyncState,
  asyncFailed,
  asyncStart,
  asyncState,
  asyncSuccess,
  createAsyncState,
} from '@ordersnproducts/utils/create-async-state';
import { createReducer, on } from '@ordersnproducts/utils/create-reducer';
import ShipmentLocationWorkSheetActions, {
  ShipmentLocationWorkSheet,
} from '../actions/shipment-location-worksheets.actions';
import { Calendar, RmsNextWorkingDayResponde, IJobsiteSalesAreasResponse, FullSalesArea } from '@cemex-core/cmx-api';

export type AsyncShipmentLocationWorkSheet = AsyncState<ShipmentLocationWorkSheet>;

export interface State extends EntityState<AsyncShipmentLocationWorkSheet> {
  activeEntityId: number | string;
  calendarsResponse: Calendar;
  cutOffResponse: RmsNextWorkingDayResponde;
  jobsiteSalesAreas: IJobsiteSalesAreasResponse;
  fullSalesAreas: FullSalesArea;
}

export const adapterShipmentLocationWorkSheet: EntityAdapter<AsyncShipmentLocationWorkSheet> =
  createEntityAdapter<AsyncShipmentLocationWorkSheet>({
    selectId: ({ payload }) => `${payload.shipmentLocationId}`,
  });

export const initialState = adapterShipmentLocationWorkSheet.getInitialState({
  activeEntityId: undefined,
  calendarsResponse: null,
  cutOffResponse: null,
  jobsiteSalesAreas: null,
  fullSalesAreas: null,
});

const selectShipmentLocationWorkSheetEntity = <S extends EntityState<any>>(shipmentLocationId: string, state: S) => {
  return state.entities[shipmentLocationId]
    ? state.entities[shipmentLocationId]
    : createAsyncState<ShipmentLocationWorkSheet>({
        shipmentLocationId,
        calendarSlots: [],
      });
};

export const shipmentLocationWorksheetReducer = createReducer(
  initialState,

  on(ShipmentLocationWorkSheetActions.setActive, (state, { shipmentLocationId }) => ({
    ...state,
    activeEntityId: shipmentLocationId,
  })),

  on(ShipmentLocationWorkSheetActions.setCalendarsResponse, (state, { calendarsResponse }) => ({
    ...state,
    calendarsResponse: calendarsResponse,
  })),

  on(ShipmentLocationWorkSheetActions.setCalendarsV2Response, (state, { calendarsResponse }) => ({
    ...state,
    calendarsResponse: calendarsResponse,
  })),

  on(ShipmentLocationWorkSheetActions.setCutOffResponse, (state, { cutOffResponse }) => ({
    ...state,
    cutOffResponse: cutOffResponse,
  })),

  on(ShipmentLocationWorkSheetActions.setJobsiteSalesAreasResponse, (state, { response }) => ({
    ...state,
    jobsiteSalesAreas: response,
  })),

  on(ShipmentLocationWorkSheetActions.setJobsiteSalesAreasWithoutPOResponse, (state, { response }) => ({
    ...state,
    jobsiteSalesAreas: response,
  })),

  on(ShipmentLocationWorkSheetActions.setFullSalesAreasResponse, (state, { response }) => ({
    ...state,
    fullSalesAreas: response,
  })),

  on(ShipmentLocationWorkSheetActions.fetch, (state, { shipmentLocationId }) => {
    const ShipmentLocationWorkSheetState = selectShipmentLocationWorkSheetEntity(`${shipmentLocationId}`, state);
    return adapterShipmentLocationWorkSheet.upsertOne(asyncStart()(ShipmentLocationWorkSheetState), state);
  }),

  on(ShipmentLocationWorkSheetActions.fetchV2, (state, { shipmentLocationId }) => {
    const ShipmentLocationWorkSheetState = selectShipmentLocationWorkSheetEntity(`${shipmentLocationId}`, state);
    return adapterShipmentLocationWorkSheet.upsertOne(asyncStart()(ShipmentLocationWorkSheetState), state);
  }),

  on(ShipmentLocationWorkSheetActions.setPlantResponse, (state, { shipmentLocationId, response }) => {
    const worksheetState = selectShipmentLocationWorkSheetEntity(`${shipmentLocationId}`, state);
    const payload = {
      shipmentLocationId: worksheetState.payload.shipmentLocationId,
      calendarSlots: worksheetState.payload.calendarSlots,
      plant: response,
    };
    return adapterShipmentLocationWorkSheet.upsertOne(
      asyncState((_) => ({
        payload,
      }))(worksheetState, payload),
      state,
    );
  }),

  on(ShipmentLocationWorkSheetActions.fetchCalendarsSuccess, (state, { payload }) => {
    const worksheetState = selectShipmentLocationWorkSheetEntity(`${payload.shipmentLocationId}`, state);
    payload.plant = worksheetState.payload.plant;
    return adapterShipmentLocationWorkSheet.upsertOne(
      asyncSuccess((_) => ({
        payload,
      }))(worksheetState, payload),
      state,
    );
  }),

  on(ShipmentLocationWorkSheetActions.fetchFailed, (state, { shipmentLocationId }) => {
    const ShipmentLocationWorkSheetState = selectShipmentLocationWorkSheetEntity(`${shipmentLocationId}`, state);

    return adapterShipmentLocationWorkSheet.upsertOne(asyncFailed()(ShipmentLocationWorkSheetState), state);
  }),

  on(ShipmentLocationWorkSheetActions.resetShipmentLocationWorkSheet, (state) => ({
    ...initialState,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return shipmentLocationWorksheetReducer(state, action);
}
